import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  IconButton,
  InputAdornment,
  Skeleton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { accordionSummaryClasses } from "@mui/material/AccordionSummary";
import { outlinedInputClasses } from "@mui/material/OutlinedInput";
import { ReactNode } from "react";
import { TagOption } from "./TagSelector.types";

type TagCategorySectionProps = {
  category: string;
  tags: TagOption[];
  count?: number;
  selectedTags: TagOption[];
  onToggleTag: (tag: TagOption) => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onViewMore?: () => void;
  headerRight?: ReactNode;
  // Wraps this category in a collapsed-by-default Accordion instead of always showing its tags -
  // opt-in so existing full-page tag pickers (e.g. content authoring's Tags field) keep every
  // category open, while a compact picker (e.g. a gallery filter modal) can default to collapsed.
  collapsible?: boolean;
  // Hides the remove ("x") button on selected chips - tags can still be added via the pool below,
  // just not removed once selected. Defaults to true (every existing caller's current behavior).
  removable?: boolean;
  // Marks this category's tag set as small and fully known up front (e.g. restricted via
  // allowedTagsByCategory) - hides the search box and View More/Loading affordance (nothing to
  // search or page through) and always renders expanded, ignoring `collapsible`.
  restricted?: boolean;
};

const SKELETON_CHIP_COUNT = 8;

const TagCategorySection = ({
  category,
  tags,
  count,
  selectedTags,
  onToggleTag,
  searchTerm,
  onSearchChange,
  loading = false,
  loadingMore = false,
  hasMore = false,
  onViewMore,
  headerRight,
  collapsible = false,
  removable = true,
  restricted = false,
}: TagCategorySectionProps) => {
  const theme = useTheme();
  const colors = theme?.palette?.customColor;
  const primary = colors?.PRIMARY ?? "#14142B";
  const onPrimary = colors?.LIGHT ?? "#FFFFFF";

  // The main grid only shows tags not already selected - selected ones live in their own row
  // above the search box instead, so they stay visible/removable regardless of what's currently
  // loaded or searched for. Identity is by `value` (the unique backend identifier), not `name`.
  const unselectedTags = tags.filter(
    (tag) => !selectedTags.some((selected) => selected.value === tag.value),
  );

  // Shared between the collapsible (Accordion summary) and non-collapsible (plain row) layouts.
  // headerRight (e.g. "Clear All") always gets its click stopped from bubbling: harmless when
  // there's no Accordion to toggle, and necessary when there is one.
  const headerRow = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
      }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant='h6semibold'>{category}</Typography>
        {typeof count === "number" && (
          <Box
            component='span'
            sx={{
              minWidth: 20,
              height: 20,
              px: 0.75,
              borderRadius: "10px",
              backgroundColor: primary,
              color: onPrimary,
              fontSize: 12,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
            {count}
          </Box>
        )}
      </Box>
      {headerRight && <Box onClick={(event) => event.stopPropagation()}>{headerRight}</Box>}
    </Box>
  );

  const body = (
    <>
      {selectedTags.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px", mt: 1.5 }}>
          {selectedTags.map((tag) => (
            <Box
              component='span'
              key={tag.value}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                padding: removable ? "6px 6px 6px 12px" : "6px 12px",
                borderRadius: "5px",
                fontSize: 14,
                fontWeight: 400,
                backgroundColor: primary,
                color: onPrimary,
              }}>
              {tag.name}
              {removable && (
                <IconButton
                  size='small'
                  aria-label={`Remove ${tag.name}`}
                  onClick={() => onToggleTag(tag)}
                  sx={{
                    padding: 0,
                    width: 18,
                    height: 18,
                    flexShrink: 0,
                    color: onPrimary,
                    "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.15)" },
                  }}>
                  <CloseIcon sx={{ fontSize: 14, color: "inherit" }} />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}

      {!restricted && (
        <TextField
          fullWidth
          placeholder={`Search ${category}`}
          value={searchTerm}
          onChange={(event) => onSearchChange(event.target.value)}
          sx={{
            mt: 1.5,
            mb: 2,
            width: "100%",
            // This app's theme renames every MUI class ("Mui" -> "Platform-x-" via
            // unstable_ClassNameGenerator, see apps/authoring-web/src/app/app.tsx), so a literal
            // ".MuiOutlinedInput-root" string never matches the rendered DOM - use the exported
            // class-name objects instead, which resolve through that same generator.
            [`& .${outlinedInputClasses.root}`]: {
              height: "46px",
              borderRadius: "8px",
              backgroundColor: "#fff",
            },
            [`& .${outlinedInputClasses.input}`]: {
              padding: "0 10px 0 0",
            },
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: colors?.LINE ?? "#D9DBE9",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start' sx={{ ml: 0, pl: "15px", mr: "0px" }}>
                <SearchIcon fontSize='small' sx={{ color: colors?.SECONDARY ?? "#9394A6" }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position='end' sx={{ ml: 0, mr: "4px" }}>
                <IconButton
                  size='small'
                  aria-label={`Clear ${category} search`}
                  onClick={() => onSearchChange("")}
                  sx={{ padding: "4px" }}>
                  <CloseIcon fontSize='small' sx={{ color: colors?.SECONDARY ?? "#9394A6" }} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      )}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "10px",
          mt: restricted ? 1.5 : 0,
        }}>
        {loading
          ? Array.from({ length: SKELETON_CHIP_COUNT }).map((_, chipIndex) => (
              <Skeleton
                key={chipIndex}
                variant='rectangular'
                width={70 + ((chipIndex * 17) % 50)}
                height={30}
                sx={{ borderRadius: "5px" }}
              />
            ))
          : unselectedTags.map((tag) => (
              <Box
                component='button'
                type='button'
                key={tag.value}
                onClick={() => onToggleTag(tag)}
                sx={{
                  padding: "6px 12px",
                  borderRadius: "5px",
                  border: `1px solid ${primary}`,
                  backgroundColor: "#fff",
                  color: primary,
                  fontSize: 14,
                  fontWeight: 400,
                  fontFamily: "inherit",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: colors?.BGWEAK ?? "#F7F7FC" },
                }}>
                {tag.name}
              </Box>
            ))}

        {!restricted && !loading && hasMore && (
          <Typography
            onClick={loadingMore ? undefined : onViewMore}
            variant='p4regular'
            component='span'
            sx={{
              color: colors?.TERTIARY ?? "#4B9EF9",
              textDecoration: "underline",
              cursor: loadingMore ? "default" : "pointer",
              opacity: loadingMore ? 0.6 : 1,
              textTransform: "capitalize",
            }}>
            {loadingMore ? "Loading..." : "View More"}
          </Typography>
        )}

        {!restricted && !loading && unselectedTags.length === 0 && (
          <Typography variant='p4regular' sx={{ color: colors?.SECONDARY ?? "#4E4B66" }}>
            No tags found.
          </Typography>
        )}
      </Box>
    </>
  );

  const showAsAccordion = collapsible && !restricted;

  return (
    <Box sx={{ mb: "15px", textTransform: "capitalize" }}>
      {showAsAccordion ? (
        <Accordion
          disableGutters
          square
          elevation={0}
          defaultExpanded={false}
          TransitionProps={{ unmountOnExit: true }}
          sx={{
            boxShadow: "none",
            "&:before": { display: "none" },
          }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              padding: 0,
              minHeight: "auto",
              [`& .${accordionSummaryClasses.content}`]: { margin: 0, width: "100%" },
            }}>
            {headerRow}
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0 }}>{body}</AccordionDetails>
        </Accordion>
      ) : (
        <>
          {headerRow}
          {body}
        </>
      )}
      <Divider sx={{ borderColor: colors?.LINE ?? "#D9DBE9", mt: "20px" }} />
    </Box>
  );
};

export default TagCategorySection;
